import { TypedStartListening } from "@reduxjs/toolkit";
import { startInactiveSpan } from "@sentry/react";
import type { IAppState } from ".";
import { BidLoadState } from "../models/utility/LoadState";

export const PROCESS_BID_TIMEOUT_MS = 60_000;

export type ProcessBidOutcome =
    | keyof typeof BidLoadState
    | "abandoned"
    | "timeout"
    | "superseded";

const isSubmitting = (state: IAppState) =>
    state.parcelBidStoreState.currentBid.submitLoadState ===
    BidLoadState.Submitting;

// A submission left for `NotSubmitted` was closed or reset before its result.
const outcomeOf = (state: IAppState): ProcessBidOutcome => {
    const { submitLoadState } = state.parcelBidStoreState.currentBid;
    return submitLoadState === BidLoadState.NotSubmitted
        ? "abandoned"
        : (BidLoadState[submitLoadState] as keyof typeof BidLoadState);
};

// Times each bid from the moment it is submitting until it leaves that state,
// whatever caused the exit, tagging how it ended as the span's `outcome`.
export const addProcessBidSpanListener = (
    startListening: TypedStartListening<IAppState>
) =>
    startListening({
        predicate: (_action, current, previous) =>
            isSubmitting(current) && !isSubmitting(previous),
        effect: async (_action, api) => {
            api.cancelActiveListeners();
            const { parcelId, parcelAmount } =
                api.getState().parcelBidStoreState.currentBid;
            const span = startInactiveSpan({
                name: "Process Bid",
                attributes: { parcelId, bidAmount: parcelAmount }
            });
            // Kept only if the listener is cancelled while still waiting.
            let outcome: ProcessBidOutcome = "superseded";
            try {
                // Read the outcome as the bid leaves `Submitting`: a cancel in
                // the same tick throws from the await and would lose it.
                const finished = await api.condition((_a, state) => {
                    if (isSubmitting(state)) return false;
                    outcome = outcomeOf(state);
                    return true;
                }, PROCESS_BID_TIMEOUT_MS);
                if (!finished) outcome = "timeout";
            } finally {
                span?.setAttribute("outcome", outcome);
                span?.end();
            }
        }
    });
